import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { User, config } from "library/api";
import { LogSeverity } from "library/logging";
import { UserContext } from "components/user";
import { useNotifications } from "components/notifications";
import { LocalCognitoCodeDelivery } from "./CognitoTypes";
import { confirmResetPassword, resetPassword, signOut } from '@aws-amplify/auth/cognito';
import Carta from "@contextualize/carta";
import { CartaIncompleteUserException } from "@contextualize/carta/dist/exceptions/CartaException";

async function configureCognito(){
  const {userPool, host} = (await config)
  console.log("Configuring Carta")
  await Carta.configure({
    cognito: {userPoolId: userPool.userPoolId, userPoolClientId: userPool.userPoolWebClientId},
    environment: host === Carta.Hosts['Production'] ? 'Production' : 'Sandbox'
  })
}

const cognitoConfigured = configureCognito();

/** A component that wraps a user context around its children components. */
const UserWrapper: FunctionComponent = ({ children }) => {
  // We use this state to store information about whether the user is authenticated and their relevant information.
  const [user, setUser] = useState<User | null>(null);

  const [carta, setCarta] = useState<Carta | null>(null);

  useEffect(()=>{
    if(!user){
      setCarta(null)
    }else{
      Carta.session().then((carta)=>setCarta(carta));
    }
  }, [user])

  // This method updates the user state.
  const { logger } = useNotifications();
  const updateAuthUser = useCallback(
    async (logErrors?: boolean) => {
      try {
        await cognitoConfigured;
        const user = await Carta.authenticatedUser();
        setUser({
          name: user.name,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email ?? "Unknown Email",
          id: user.id,
          groups: user.groups,
        });
      } catch (error: any) {
        if (logErrors) {
          logger.log({
            source: "User Wrapper",
            title: "Failed to fetch user information",
            severity: LogSeverity.Error,
            message: error.message,
          });
        } else setUser(null);
      }
    }, [logger]);

  useEffect(()=>{
    (async () => {
      await cognitoConfigured;
      await updateAuthUser();
    })();
  }, [updateAuthUser])


  // Prepare functions to handle authentication related requests.
  const handleSignIn = async (username: string, password: string) => {
    // Execute sign in request.
    try {
      await Carta.signIn({ username, password });
      const user = await Carta.authenticatedUser();
      setUser({
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email ?? "Unknown Email",
        id: user.id,
        groups: user.groups,
      });
      return null;
    } catch (e) {
      if (e instanceof CartaIncompleteUserException) {
        return e.nextStep;
      }
    }
  };

  const handleCompleteSignIn = async (
    password: string,
    firstname: string,
    lastname: string
  ) => {
    // Execute complete sign in request.
    
    await Carta.signIn({confirmSignIn: {
        challengeResponse: password,
        options: {
          userAttributes: {
            given_name: firstname,
            family_name: lastname
          }
        }
    }});
    
    await updateAuthUser();
  };
  const handleForgotPassword = async (username: string) => {
    const data = await resetPassword({username});
    return {
      attributeName: data.nextStep.codeDeliveryDetails.attributeName,
      deliveryMedium: data.nextStep.codeDeliveryDetails.deliveryMedium,
      destination: data.nextStep.codeDeliveryDetails.destination,
    } as LocalCognitoCodeDelivery;
  };
  const handleResetPassword = async (
    username: string,
    code: string,
    password: string
  ) => {
    setUser(null);
    return await confirmResetPassword({username, confirmationCode: code, newPassword: password});
  };
  const handleSignOut = async (apiless?: boolean) => {
    // Execute sign out request.
    try {
      if (!apiless) await signOut({global: true});
      setUser(null);
    } catch (error: any) {
      logger.log({
        source: "User Wrapper",
        title: "Failed to sign out",
        severity: LogSeverity.Error,
        message: error.message,
      });
    }
  };

  return (
    <UserContext.Provider
      value={{
        user: user,
        authenticated: user !== null,
        carta: carta,
        signIn: handleSignIn,
        signOut: handleSignOut,
        completeSignIn: handleCompleteSignIn,
        forgotPassword: handleForgotPassword,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserWrapper;
